/**
 * Vending Fleet Tour — client-side fallback steps.
 *
 * v2.5.901 — Mirrors `VENDING_TOUR_STEPS` in backend/routes/tour.py so the
 * Replay Tour button always works even on stale Plesk deploys. Fields:
 *   - id, title, target, page, narration, position
 *   - narration is the full paragraph read aloud (used for TTS fallback too)
 *   - position drives spotlight tooltip placement (top/bottom/left/right/center)
 */
export const VENDING_TOUR_STEPS_FALLBACK = [
  {
    id: 'vending_intro',
    title: 'Welcome to the Vending Fleet',
    target: "[data-testid='vending-fleet-page']",
    page: '/vending',
    position: 'center',
    narration: "Welcome to the Vending module. This is the operating system for your automated retail fleet — every machine, every location, every SKU, every cash and cashless transaction, in one live dashboard. Whether you run five snack machines in an office park or five hundred combo units across a city, everything you need is here.",
  },
  {
    id: 'vending_fleet_stats',
    title: 'Your Fleet at a Glance',
    target: "[data-testid='vending-fleet-stats']",
    page: '/vending',
    position: 'bottom',
    narration: "The four cards at the top give you your fleet health in one glance. Total machines, how many are actually deployed at client sites, how many are sitting in the warehouse waiting to earn, and how many customer locations you're serving. When telemetry is live, revenue and vend counts populate here in real time.",
  },
  {
    id: 'vending_machines_tab',
    title: 'The Machines Tab — Your Assets',
    target: "[data-testid='tab-machines']",
    page: '/vending',
    position: 'bottom',
    narration: "The Machines tab lists every physical asset in your fleet. Each card shows the chassis serial, current status — deployed, warehouse, servicing, or decommissioned — the vendor and model, the telemetry provider like Jetinno or Aurency, the currency, and where the machine is right now. Tap any card to open the full asset record.",
  },
  {
    id: 'vending_add_machine',
    title: 'Adding a Machine',
    target: "[data-testid='vending-add-machine']",
    page: '/vending',
    position: 'left',
    narration: "Tap Add Machine to bring a new asset into your fleet. Pick from 32 pre-configured models via the catalog picker — Jetinno, Necta, FAS, Bianchi, TCN, Crane, AMS, Sielaff, Aurency and more — and slots auto-populate from the model's default planogram. Chassis serial, capital cost, currency all captured in one shot.",
  },
  {
    id: 'vending_locations_tab',
    title: 'Locations — Where Machines Earn',
    target: "[data-testid='tab-locations']",
    page: '/vending',
    position: 'bottom',
    narration: "The Locations tab lists every site where your fleet is deployed. Geolocation coordinates, site type — office, hospital, factory, school, gym — and the primary contact with birthday. That birthday isn't decoration: the Retention module sends an automated birthday message from you every year.",
  },
  {
    id: 'vending_clients_tab',
    title: 'Clients — Your Host Companies',
    target: "[data-testid='tab-clients']",
    page: '/vending',
    position: 'bottom',
    narration: "The Clients tab is your CRM view of every business that hosts your machines. Each client carries six named contacts — Primary, AP, AR, Facilities, Decision Maker, Escalation — plus two banking blocks for commission payouts and invoice receipts. Konnect invoices the right party at the right cycle.",
  },
  {
    id: 'vending_products_tab',
    title: 'Products — Your SKU Catalog',
    target: "[data-testid='tab-products']",
    page: '/vending',
    position: 'bottom',
    narration: "Every SKU you sell lives here — chips, drinks, coffee recipes, PPE, fresh food. Unit-based SKUs decrement inventory by one per vend. Recipe-based products, like coffee, deduct fractional ingredients from onboard canisters using a Bill of Materials. Expiration tracking enforces First-In-First-Out at every restock.",
  },
  {
    id: 'vending_add_product',
    title: 'Cost, Retail, and Margin',
    target: "[data-testid='vending-add-product']",
    page: '/vending',
    position: 'left',
    narration: "When you add a product, enter cost and retail price — Konnect calculates margin automatically. When telemetry starts feeding real vends, your per-machine Profit and Loss uses these numbers to compute exact profit, factoring commission, driver visit cost, hardware amortization, and telemetry fees.",
  },
  {
    id: 'vending_movements',
    title: 'Refresh, Print QR, Replay Tour',
    target: "[data-testid='vending-replay-tour-btn']",
    page: '/vending',
    position: 'bottom',
    narration: "The action row at the top gives you quick controls: Refresh reloads the fleet dashboard, Replay Tour reopens this walkthrough any time, and each machine card carries its own Print QR button so drivers can scan straight into the DEX Companion on their phone.",
  },
  {
    id: 'vending_whats_next',
    title: 'You Are Ready to Run the Fleet',
    target: "[data-testid='vending-title']",
    page: '/vending',
    position: 'center',
    narration: "You've seen the foundation. From here you get live telemetry from Jetinno and Aurency, a visual planogram editor, route optimization and pre-kitting for your drivers, automated commission calculations, and per-machine profitability wired into CRM, Marketing, Service and Reputation. Welcome to the future of unattended retail management.",
  },
];
